// BM-H3883 (ruling A5, 5 Oct 2026): the trustee list, for a client who says the trustees are not the same
// people as the executors. Each trustee is an individual (a person from the register, so their name, address
// and date of birth are held once) or a company. Primary or substitute; a substitute says which trustee it
// replaces and its rank. The client may name a company but cannot say it is a Trust Corporation: there is no
// such question, because that status is checked by a partner and never taken from the client. Whether a
// trustee lives in the UK is not asked here either: the fee earner confirms that.

import PersonPicker from "./PersonPicker";
import { NumberInput, QuestionRow, Select, SmallButton, TextInput, YesNo, replaceAt, removeAt } from "./fields";

export const KIND_OPTIONS = [
  { value: "individual", label: "A person" },
  { value: "corporate", label: "A company" },
];
export const POSITION_OPTIONS = [
  { value: "primary", label: "A trustee" },
  { value: "substitute", label: "A substitute, if a trustee cannot act" },
];

export function nextTrusteeId(items) {
  const used = (items || []).map((t) => Number(String(t.id || "").replace(/^tr_/, ""))).filter((n) => Number.isInteger(n));
  return `tr_${(used.length ? Math.max(...used) : 0) + 1}`;
}

export function blankTrustee(items, over = {}) {
  return {
    id: nextTrusteeId(items), kind: "individual", position: "primary", person_id: null,
    company_name: null, company_number: null, jurisdiction: null, address: null,
    rank: null, replaces: null, also_executor: null, ...over,
  };
}

// The client's executors, as trustee records: one click so the same people do not have to be typed twice.
export function trusteesFromExecutors(executors = {}) {
  const out = [];
  const add = (list, position) => (list || []).forEach((e) => {
    if (!e.person_id) return;
    out.push(blankTrustee(out, { person_id: e.person_id, position, also_executor: true }));
  });
  add(executors.primary, "primary");
  add(executors.substitutes, "substitute");
  const firstPrimary = out.find((t) => t.position === "primary");
  let rank = 0;
  return out.map((t) => (t.position === "substitute"
    ? { ...t, rank: (rank += 1), replaces: firstPrimary ? firstPrimary.id : null }
    : t));
}

function nameOf(t, payload) {
  if (t.kind === "corporate") return t.company_name || "the company";
  const p = (payload.couple.people_register || []).find((x) => x.id === t.person_id);
  return p?.full_legal_name || "this trustee";
}

export default function S3Trustees({ section, set, payload, update, testatorId }) {
  const items = section.trustees || [];
  const write = (next) => set({ trustees: next });
  const patch = (i, fields) => write(replaceAt(items, i, { ...items[i], ...fields }));
  const primaries = items.filter((t) => t.position === "primary");
  const taken = items.map((t) => t.person_id).filter(Boolean);

  return (
    <div data-testid="s3-trustees" style={{ marginTop: 18 }}>
      <h3 style={{ fontSize: 15, margin: "0 0 6px" }}>Your trustees</h3>
      <p style={{ fontSize: 13, margin: "0 0 10px" }}>
        You said your trustees are not the same people as your executors. Add each trustee. You can add a company
        if you wish; your fee earner checks any company before it is used.
      </p>
      {items.length === 0 && (section.primary?.length || section.substitutes?.length) ? (
        <div style={{ marginBottom: 10 }}>
          <SmallButton testid="s3-trustees-copy" onClick={() => write(trusteesFromExecutors(section))}>Start from my executors</SmallButton>
        </div>
      ) : null}

      {items.map((t, i) => (
        <div key={t.id || i} data-testid={`s3-trustees-row-${i}`} style={{ border: "1px solid #ccc", borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <QuestionRow qid="s3_executors.trustees.kind" label="Is this trustee a person or a company?" testid={`s3-trustees-${i}-q-kind`}>
            <Select testid={`s3-trustees-${i}-kind`} value={t.kind} options={KIND_OPTIONS}
              onChange={(v) => patch(i, { kind: v || "individual", person_id: null, company_name: null, company_number: null, jurisdiction: null, address: null })} />
          </QuestionRow>

          {t.kind === "corporate" ? (
            <>
              <QuestionRow qid="s3_executors.trustees.company_name" label="The company's full name" testid={`s3-trustees-${i}-q-company-name`}>
                <TextInput testid={`s3-trustees-${i}-company-name`} value={t.company_name} onChange={(v) => patch(i, { company_name: v })} />
              </QuestionRow>
              <QuestionRow qid="s3_executors.trustees.company_number" label="Its company number" testid={`s3-trustees-${i}-q-company-number`}>
                <TextInput testid={`s3-trustees-${i}-company-number`} value={t.company_number} onChange={(v) => patch(i, { company_number: v })} />
              </QuestionRow>
              <QuestionRow qid="s3_executors.trustees.jurisdiction" label="Where is it registered?" testid={`s3-trustees-${i}-q-jurisdiction`}>
                <TextInput testid={`s3-trustees-${i}-jurisdiction`} value={t.jurisdiction} onChange={(v) => patch(i, { jurisdiction: v })} />
              </QuestionRow>
              <QuestionRow qid="s3_executors.trustees.address" label="Its address" testid={`s3-trustees-${i}-q-address`}>
                <TextInput testid={`s3-trustees-${i}-address`} value={t.address} onChange={(v) => patch(i, { address: v })} />
              </QuestionRow>
            </>
          ) : (
            <PersonPicker
              payload={payload} update={update} value={t.person_id}
              onChange={(pid) => patch(i, { person_id: pid })}
              qid="s3_executors.trustees.person" label="Who is this trustee?"
              exclude={[testatorId, ...taken.filter((id) => id !== t.person_id)].filter(Boolean)}
              testid={`s3-trustees-${i}-picker`}
            />
          )}

          <QuestionRow qid="s3_executors.trustees.position" label="Will they act as a trustee, or only if another cannot?" testid={`s3-trustees-${i}-q-position`}>
            <Select testid={`s3-trustees-${i}-position`} value={t.position} options={POSITION_OPTIONS}
              onChange={(v) => patch(i, { position: v || "primary", ...(v === "substitute" ? {} : { rank: null, replaces: null }) })} />
          </QuestionRow>

          {t.position === "substitute" && (
            <>
              <QuestionRow qid="s3_executors.trustees.replaces" label="Which trustee would they replace?" testid={`s3-trustees-${i}-q-replaces`}>
                <Select testid={`s3-trustees-${i}-replaces`} value={t.replaces}
                  options={primaries.map((p) => ({ value: p.id, label: nameOf(p, payload) }))}
                  onChange={(v) => patch(i, { replaces: v })} />
              </QuestionRow>
              <QuestionRow qid="s3_executors.trustees.rank" label="Their order, if more than one substitute (1 steps in first)" testid={`s3-trustees-${i}-q-rank`}>
                <NumberInput testid={`s3-trustees-${i}-rank`} min="1" step="1" value={t.rank}
                  onChange={(v) => patch(i, { rank: v === "" || v == null ? null : Number(v) })} />
              </QuestionRow>
            </>
          )}

          <QuestionRow qid="s3_executors.trustees.also_executor" label="Are they also one of your executors?" testid={`s3-trustees-${i}-q-also-executor`}>
            <YesNo testid={`s3-trustees-${i}-also-executor`} value={t.also_executor} onChange={(v) => patch(i, { also_executor: v })} />
          </QuestionRow>

          <SmallButton testid={`s3-trustees-${i}-remove`} onClick={() => write(removeAt(items, i))}>Remove this trustee</SmallButton>
        </div>
      ))}
      <SmallButton testid="s3-trustees-add" onClick={() => write([...items, blankTrustee(items)])}>Add a trustee</SmallButton>
    </div>
  );
}
